import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useCreateNewOrderMutation } from '../../redux/api/bookingApi';
import { setTier, setNotes, resetRideFlow } from '../../redux/features/rideFlowSlice';
import { RIDE_TIERS, parseDistanceKm, tierFare, formatPKR, buildRideOrder } from './rideConfig';
import RideMap from './RideMap';
import '../../css/ride.css';

export default function RideSelect() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((s) => s.auth);
  const { pickup, dropoff, routeMeta, tier, notes } = useSelector((s) => s.rideFlow);
  const km = parseDistanceKm(routeMeta?.distance);

  const [createNewOrder, { isLoading, error, isSuccess, data }] = useCreateNewOrderMutation();

  useEffect(() => {
    if (error) toast.error(error?.data?.message || 'Could not book the ride');
  }, [error]);

  useEffect(() => {
    if (isSuccess && data?.order?._id) {
      dispatch(resetRideFlow());
      navigate(`/ride/track/${data.order._id}`, { replace: true });
    }
  }, [isSuccess, data, dispatch, navigate]);

  // Guard: if we have no endpoints, go back home.
  useEffect(() => {
    if (!pickup?.label || !dropoff?.label || !routeMeta?.distance) {
      navigate('/ride', { replace: true });
    }
  }, [pickup, dropoff, routeMeta, navigate]);

  if (!pickup?.label || !dropoff?.label || !routeMeta?.distance) return null;

  const onBook = () => {
    if (!isAuthenticated) {
      toast('Please sign in to book');
      navigate('/login', { state: { from: '/ride/select' } });
      return;
    }
    const order = buildRideOrder({
      pickup,
      dropoff,
      distanceText: routeMeta.distance,
      tierId: tier,
      notes,
    });
    createNewOrder(order);
  };

  const selected = RIDE_TIERS.find((t) => t.id === tier) || RIDE_TIERS[0];
  const price = formatPKR(tierFare(km, selected.vehicle));

  return (
    <>
      <div className="ride-topbar">
        <button className="ride-icon-btn" onClick={() => navigate('/ride')} aria-label="Back">
          <i className="bi bi-chevron-left" />
        </button>
        <div className="ride-brand-pill"><span>Choose a ride</span></div>
        <button className="ride-icon-btn" onClick={() => navigate('/ride/rides')} aria-label="Rides">
          <i className="bi bi-journal-text" />
        </button>
      </div>

      <div className="ride-map-area">
        <RideMap
          pickup={pickup}
          dropoff={dropoff}
          route={{ origin: pickup.label, destination: dropoff.label }}
        />
      </div>

      <div className="ride-panel">
        <div className="ride-grabber" />
        <div className="ride-panel-inner">
          <div className="ride-route-summary">
            <i className="bi bi-circle-fill" style={{ color: 'var(--color-success)', fontSize: '.6rem' }} />
            <b style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pickup.short}</b>
            <span className="sep">→</span>
            <i className="bi bi-circle-fill" style={{ color: 'var(--color-danger)', fontSize: '.6rem' }} />
            <b style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dropoff.short}</b>
            <span className="sep">·</span>
            <span>{routeMeta.distance} · {routeMeta.duration}</span>
          </div>

          <div className="ride-section-label">Choose your ride</div>
          {RIDE_TIERS.map((t) => {
            const fare = formatPKR(tierFare(km, t.vehicle));
            const isSel = t.id === tier;
            return (
              <button
                key={t.id}
                className={`ride-tier ${isSel ? 'selected' : ''}`}
                onClick={() => dispatch(setTier(t.id))}
              >
                <span className="ride-tier-ico"><i className={`bi ${t.icon}`} /></span>
                <span className="ride-tier-main">
                  <div className="ride-tier-name">{t.name}</div>
                  <div className="ride-tier-tag">{t.tagline}</div>
                </span>
                <span className="ride-tier-fare">
                  <div className="ride-tier-price">{fare}</div>
                  <div className="ride-tier-eta">{t.capacity}</div>
                </span>
              </button>
            );
          })}

          <div className="ride-section-label">Notes for the driver</div>
          <textarea
            className="form-control"
            rows={2}
            placeholder="e.g. Call me when you arrive"
            value={notes}
            onChange={(e) => dispatch(setNotes(e.target.value))}
            style={{ fontSize: '.9rem' }}
          />

          <div className="ride-section-label">Payment</div>
          <div className="ride-pay-row">
            <span className="ride-row-ico"><i className="bi bi-cash-coin" /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '.9rem' }}>Cash on delivery</div>
              <div className="ride-pay-note">Pay the driver after the trip</div>
            </div>
            <i className="bi bi-check-circle-fill text-success" />
          </div>

          <button className="ride-cta mt-4" onClick={onBook} disabled={isLoading}>
            {isLoading ? 'Booking…' : (
              <>Book {selected.name} · {price}</>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
